<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/system/keys/new"
    create-label="Crear llave de sistema"
    create-icon="mdi-key-plus"
    remove-title="Eliminar llave de sistema"
  />
</template>

<script setup lang="ts">
import type { SystemKey } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import { useApiErrors } from "@/composables/useApiErrors";
import { useNotificationsStore } from "@/stores/notifications";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { systemKeyTypeLabel } from "@/utils/labels";

const { report } = useApiErrors();
const notifications = useNotificationsStore();

const view = useIndexView<SystemKey, "alias" | "type">({
  title: "Llaves de sistema",
  rowKey: "key_id",
  defaultSort: "key_id,asc",
  fetchPage: (params) => banco.system.keys.find(params),
  remove: (item) => banco.system.keys.destroy(item.key_id),
  removedMessage: (item) => `Se eliminó la llave de sistema ${item.alias ?? item.key_id}.`,
  filters: [
    { field: "alias", label: "Alias" },
    { field: "type", label: "Tipo" },
  ],
  confirm: {
    expected: (item) => item.alias ?? String(item.key_id),
    label: "Alias de la llave",
    hint: "Para confirmar ingresa el <b>alias</b> de la llave a eliminar.",
    error: "El alias de la llave no es correcto.",
    warning: (item) =>
      `Esta acción eliminará la llave de sistema ${item.alias ?? item.key_id}. Si es la llave por defecto, las operaciones que la usen dejarán de funcionar.`,
  },
});

/**
 * Marcar una llave como la por defecto de su tipo.
 *
 * Es la ÚNICA modificación posible sobre una llave de sistema:
 * SystemService.updateKey sólo atiende el campo `default`. Vive acá y no en una
 * vista de edición porque no existe `GET /system/keys/{id}` con el que cargar un
 * formulario — ver la nota en Edit.vue.
 *
 * La API responde 412 si ya hay otra default del mismo tipo, así que el mensaje
 * del servidor explica exactamente qué pasó.
 */
async function makeDefault(item: SystemKey) {
  try {
    await banco.system.keys.setDefault(item.key_id, { default: true });
    notifications.success(
      `La llave ${item.alias ?? item.key_id} quedó como default de tipo ${systemKeyTypeLabel(item.type)}.`,
      "Guardado"
    );
    await view.loadData();
  } catch (err) {
    report(err, {
      title: "Llaves de sistema",
      fallback: "No fue posible marcar la llave como default.",
    });
  }
}

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  { header: { text: "ID", value: "key_id", class: "text-no-wrap" }, row: { type: "TEXT" } },
  { header: { text: "Alias", value: "alias", class: "text-no-wrap" }, row: { type: "TEXT" } },
  {
    header: { text: "Tipo", value: "type", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { TRANSPORT: "blue", WRAPPING: "purple" },
      transform: (val) => systemKeyTypeLabel(val),
    },
  },
  {
    // El nombre en el cable es `default`: SystemKeyDTO lleva
    // @JsonProperty("default") sobre el campo Java `default_key`.
    header: { text: "Default", value: "default", class: "text-no-wrap", sortable: false },
    row: {
      type: "CHIP",
      label: true,
      color: { true: "success", false: "grey" },
      transform: (val) => (val ? "SÍ" : "NO"),
    },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        {
          label: "Marcar como default",
          icon: "mdi-star",
          action: (item) => void makeDefault(item as SystemKey),
          disabled: (item) => !!(item as SystemKey).default,
        },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as SystemKey),
        },
      ],
    },
  },
];
</script>
